'use client';

import { useMemo, useRef, useState } from 'react';
import {
  decimalToFraction,
  formatDecimal,
  formatFraction,
  formatMixed,
  parseFraction,
  type FractionValue,
} from '@/lib/browser-fraction';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Kind = 'decimal' | 'fraction' | 'percent';
const EXAMPLE: Record<Kind, string> = { decimal: '0.625', fraction: '3/8', percent: '37.5' };
const LABEL: Record<Kind, string> = {
  decimal: 'Decimal',
  fraction: 'Fraction',
  percent: 'Percent',
};

export default function BrowserFractionConverter({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const initial =
    (['decimal', 'fraction', 'percent'] as Kind[]).find((k) => k === config?.variant) ?? 'decimal';
  const [kind, setKind] = useState<Kind>(initial);
  const [input, setInput] = useState(EXAMPLE[initial]);
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'decimal-to-fraction', '').catch(() => undefined);
  };

  const { result, error } = useMemo(() => {
    try {
      let exact: FractionValue;
      let approx: FractionValue | null = null;
      let steps: string[];
      if (kind === 'fraction') {
        exact = parseFraction(input);
        steps = [
          `${formatFraction(exact)} = ${exact.num} ÷ ${exact.den} = ${formatDecimal(exact.num / exact.den)}`,
        ];
      } else {
        const raw = input.trim().replace(/%$/, '').trim();
        const asDecimal = kind === 'percent' ? shiftLeft(raw, 2) : raw;
        const r = decimalToFraction(asDecimal);
        exact = r.exact;
        approx = r.approx;
        const digits = (asDecimal.split('.')[1] ?? '').length;
        steps = [
          kind === 'percent' ? `${raw}% = ${raw} ÷ 100 = ${formatDecimal(Number(asDecimal))}` : '',
          digits
            ? `${formatDecimal(Number(asDecimal))} = ${Math.round(Number(asDecimal) * 10 ** digits)}/${10 ** digits}`
            : '',
          `Simplify: ${formatFraction(exact)}`,
        ].filter(Boolean);
      }
      const value = exact.num / exact.den;
      return { result: { exact, approx, value, steps }, error: null };
    } catch (e) {
      return { result: null, error: e instanceof Error ? e.message : 'Check the input.' };
    }
  }, [input, kind]);

  const rows: Array<[string, string]> = result
    ? [
        ['Fraction', formatFraction(result.exact)],
        ...(formatMixed(result.exact)
          ? ([['Mixed number', formatMixed(result.exact) as string]] as Array<[string, string]>)
          : []),
        ...(result.approx
          ? ([['Repeating decimal, as a fraction', formatFraction(result.approx)]] as Array<
              [string, string]
            >)
          : []),
        ['Decimal', formatDecimal(result.value)],
        ['Percent', `${formatDecimal(result.value * 100)}%`],
      ]
    : [];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Math</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Decimal to fraction converter'}</h2>
        <p className="tool-live-subtitle">
          Convert between decimals, fractions and percents, simplified, with the steps shown.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-actions" role="group" aria-label="Convert from">
            {(['decimal', 'fraction', 'percent'] as Kind[]).map((k) => (
              <button
                aria-pressed={kind === k}
                className={kind === k ? 'button' : 'button button-secondary'}
                key={k}
                onClick={() => {
                  report();
                  setKind(k);
                  setInput(EXAMPLE[k]);
                }}
                type="button"
              >
                From {LABEL[k].toLowerCase()}
              </button>
            ))}
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>{LABEL[kind]}</span>
            <input
              inputMode={kind === 'fraction' ? 'text' : 'decimal'}
              onChange={(e) => {
                report();
                setInput(e.target.value);
              }}
              spellCheck={false}
              type="text"
              value={input}
            />
          </label>
          {error ? <p className="status-message status-error">{error}</p> : null}
        </section>
        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            {rows.map(([label, value]) => (
              <div className="tool-live-field" key={label}>
                <span>{label}</span>
                <input readOnly type="text" value={value} />
              </div>
            ))}
          </div>
          {result ? (
            <ol className="tool-live-panel-note">
              {result.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          ) : null}
        </section>
      </div>
    </div>
  );
}

/** Moves the decimal point left by `places` without floating-point noise. */
function shiftLeft(raw: string, places: number): string {
  const m = /^(-)?(\d*)(?:\.(\d+))?$/.exec(raw.replace(/,/g, ''));
  if (!m || (!m[2] && !m[3])) throw new Error('Enter a percent like 37.5.');
  const digits = (m[2] || '0').padStart(places + 1, '0');
  const int = digits.slice(0, -places).replace(/^0+(?=\d)/, '');
  return `${m[1] ?? ''}${int}.${digits.slice(-places)}${m[3] ?? ''}`;
}
